





<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const showNav = computed(() => route.meta.hideNav !== true)


const myProfilePath = ref('/stats')

async function loadMyProfilePath() {
  try {
    const res = await fetch('/api/stats/my-profile', {
      credentials: 'include'
    })

    if (!res.ok) {
      myProfilePath.value = `/stats/player/${encodeURIComponent(authStore.username || '')}`
      return
    }

    const data = await res.json()
    myProfilePath.value = data.profile_path || `/stats/player/${encodeURIComponent(authStore.username || '')}`
  } catch {
    myProfilePath.value = `/stats/player/${encodeURIComponent(authStore.username || '')}`
  }
}

onMounted(loadMyProfilePath)

async function logout() {
  await authStore.logout()
  router.push('/')
}
</script>

<template>
  <header v-if="showNav && authStore.isAuthenticated" class="header">
    <router-link to="/library" class="logo">
      <span class="bumpy">BUMPY</span>
      <span class="analyzer">ANALYZER</span>
      </router-link>
    <nav class="nav">
      <router-link to="/library" class="nav-link" active-class="active">
        Library
      </router-link>
      <router-link to="/capture" class="nav-link" active-class="active">
        Capture
      </router-link>
      <div class="leaderboard-dropdown">
        <button class="nav-link leaderboard-dropdown-btn" type="button">
          Leaderboard
        </button>

        <div class="leaderboard-dropdown-menu">
          <a href="/stats/3v3" class="leaderboard-dropdown-item">
            3v3
          </a>
          <a href="/stats/2v2" class="leaderboard-dropdown-item">
            2v2
          </a>
          <a href="/stats/1v1" class="leaderboard-dropdown-item">
            1v1
          </a>
        </div>
      </div>

      <div class="nav-dropdown">
        <button class="nav-link nav-dropdown-btn" type="button">
          Statistics
        </button>

        <div class="nav-dropdown-menu">
          <router-link to="/stats/stat-leaders/3v3" class="nav-dropdown-item" active-class="active">
            3v3 Stat Leaders
          </router-link>
          <router-link to="/stats/stat-leaders/2v2" class="nav-dropdown-item" active-class="active">
            2v2 Stat Leaders
          </router-link>
          <router-link to="/stats/stat-leaders/1v1" class="nav-dropdown-item" active-class="active">
            1v1 Stat Leaders
          </router-link>

          <router-link to="/stats/fraud-watch" class="nav-dropdown-item" active-class="active">
            Fraud Watch
          </router-link>
          <router-link to="/stats/h2h" class="nav-dropdown-item" active-class="active">
            H2H
          </router-link>
        </div>
      </div>

      <router-link
        v-if="authStore.isAdmin"
        to="/stats/aliases"
        class="nav-link"
        active-class="active"
      >
        Aliases
      </router-link>
      <router-link
        v-if="authStore.isAdmin"
        to="/admin"
        class="nav-link"
        active-class="active"
      >
        Admin
      </router-link>

      <div class="nav-user">
                <router-link :to="myProfilePath" class="nav-link my-profile-link" active-class="active">
          My Profile
        </router-link>

        <span class="nav-username">{{ authStore.username }}</span>
        <button class="btn-logout" @click="logout">Logout</button>
      </div>
    </nav>
  </header>
</template>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 70px;
  background: rgba(10, 10, 12, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--accent-border);
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
}

.logo {
  font-family: var(--font-header);
  font-size: 18px;
  font-weight: 900;
  text-decoration: none;
  display: flex;
  align-items: center;
  letter-spacing: 2px;
}

.logo .bumpy {
  color: #fff;
}

.logo .analyzer {
  color: var(--neon-blue);
  margin-left: 8px;
  font-size: 18px;
  opacity: 0.8;
}

.nav {
  display: flex;
  gap: 8px;
  align-items: center;
}

.nav-link {
  color: var(--text-subtle);
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  padding: 10px 18px;
  border-radius: 8px;
  transition: all 0.25s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.nav-link:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.nav-link.active {
  color: #000;
  background: #fff;
}

.nav-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 0 0 20px;
  margin-left: 12px;
  border-left: 1px solid var(--accent-border);
  height: 30px;
}

.nav-username {
  color: var(--neon-blue);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.btn-logout {
  background: transparent;
  border: 1px solid var(--accent-border);
  color: var(--text-subtle);
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-logout:hover {
  background: rgba(255, 68, 102, 0.1);
  border-color: var(--neon-red);
  color: var(--neon-red);
}

@media (max-width: 768px) {
  .header {
    padding: 0 20px;
    height: 60px;
  }
  
  .logo .analyzer {
    display: none;
  }
  
  .nav-link {
    padding: 8px 12px;
    font-size: 11px;
  }
  
  .nav-user {
    padding-left: 12px;
    margin-left: 8px;
  }
  
  .nav-username {
    display: none;
  }
}

.my-profile-link {
  border: 1px solid rgba(0, 212, 255, 0.35);
  color: var(--neon-blue);
}


@media (max-width: 900px) {
  .nav-dropdown-menu {
    right: 0;
    left: auto;
  }
}


/* Statistics dropdown click fix */
.nav-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding-bottom: 14px;
  margin-bottom: -14px;
}

.nav-dropdown-btn {
  cursor: default;
}

.nav-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 190px;
  display: none;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: rgba(10, 10, 12, 0.98);
  box-shadow: 0 18px 40px rgba(0,0,0,0.45);
  backdrop-filter: blur(18px);
  z-index: 999999;
  pointer-events: auto;
}

.nav-dropdown:hover .nav-dropdown-menu,
.nav-dropdown:focus-within .nav-dropdown-menu,
.nav-dropdown-menu:hover {
  display: flex;
}

.nav-dropdown-item {
  display: block;
  color: var(--text-subtle);
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 800;
  padding: 10px 12px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  white-space: nowrap;
  pointer-events: auto;
}

.nav-dropdown-item:hover {
  color: #fff;
  background: rgba(255,255,255,0.09);
}

.nav-dropdown-item.active {
  color: #000;
  background: #fff;
}


/* Leaderboard 3v3/1v1 dropdown */
.leaderboard-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding-bottom: 14px;
  margin-bottom: -14px;
}

.leaderboard-dropdown-btn {
  cursor: default;
}

.leaderboard-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 130px;
  display: none;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  background: rgba(10, 10, 12, 0.98);
  box-shadow: 0 18px 40px rgba(0,0,0,0.45);
  backdrop-filter: blur(18px);
  z-index: 999999;
}

.leaderboard-dropdown:hover .leaderboard-dropdown-menu,
.leaderboard-dropdown:focus-within .leaderboard-dropdown-menu,
.leaderboard-dropdown-menu:hover {
  display: flex;
}

.leaderboard-dropdown-item {
  display: block;
  color: var(--text-subtle);
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 900;
  padding: 10px 12px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  white-space: nowrap;
}

.leaderboard-dropdown-item:hover {
  color: #fff;
  background: rgba(255,255,255,0.09);
}


</style>
